import Phaser from 'phaser';
import { PALETTE } from '../theme';
import { audio } from '../audio/instances';
import { t } from '../i18n';
import { getTutorialCatalog } from '../turn/scenarioCatalog';
import type { GameConfig } from '../turn/types';

/** Thumbnail images for each tutorial lesson (1-based index). */
const LESSON_THUMBNAILS: Record<number, string> = {
  1: '/graphics/move-order-icon.png',
  2: '/graphics/attack-order-icon.png',
  3: '/graphics/capture-order-icon.png',
  4: '/graphics/recruitment-order-icon.png',
  5: '/graphics/repair-and-heal-order-icon.png',
  6: '/graphics/transport-units-icon.png',
  7: '/graphics/bunker-building-order-icon.png',
};

export class TutorialScene extends Phaser.Scene {
  private dom: HTMLDivElement | null = null;
  private selectedLesson = 0; // 0-based index
  private detailContainer: HTMLDivElement | null = null;
  private thumbnailContainer: HTMLDivElement | null = null;

  constructor() {
    super({ key: 'Tutorial' });
  }

  create(): void {
    this.cameras.main.setBackgroundColor(PALETTE.bg);
    this.selectedLesson = 0;
    this.dom = this.buildDom();
    document.body.appendChild(this.dom);
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => this.teardown());
  }

  private buildDom(): HTMLDivElement {
    const root = document.createElement('div');
    root.dataset.tutorialOverlay = 'true';
    Object.assign(root.style, {
      position: 'fixed', inset: '0',
      display: 'flex', flexDirection: 'column',
      color: '#d8dee9',
      fontFamily: "system-ui, -apple-system, 'Segoe UI', sans-serif",
      background: '#0b0d10 url("wallpaper.png") center/cover no-repeat', zIndex: '10',
    } as Partial<CSSStyleDeclaration>);

    const hoverStyle = document.createElement('style');
    hoverStyle.textContent = `.img-btn:hover{filter:brightness(1.2)}.img-btn:active{filter:brightness(0.9);transform:scale(0.97)}`;
    root.appendChild(hoverStyle);

    // Header
    const header = document.createElement('div');
    Object.assign(header.style, {
      display: 'flex', alignItems: 'center', gap: '16px',
      padding: '14px 20px', borderBottom: '1px solid #2a3140',
    });
    const title = document.createElement('span');
    title.textContent = t('tutorial.header');
    Object.assign(title.style, { fontSize: '18px', fontWeight: '700', letterSpacing: '2px' });
    header.appendChild(title);
    root.appendChild(header);

    // Body: two columns
    const body = document.createElement('div');
    Object.assign(body.style, { display: 'flex', flex: '1', overflow: 'hidden' });

    // Left: lesson list
    const listPanel = document.createElement('div');
    Object.assign(listPanel.style, {
      width: '260px', minWidth: '260px', borderRight: '1px solid #2a3140',
      overflowY: 'auto', padding: '8px',
    });
    const listHeader = document.createElement('div');
    listHeader.textContent = t('tutorial.lessons');
    Object.assign(listHeader.style, {
      fontSize: '11px', fontWeight: '600', textTransform: 'uppercase',
      letterSpacing: '1.5px', opacity: '0.5',
      padding: '6px 14px 8px', marginBottom: '2px',
    });
    listPanel.appendChild(listHeader);

    const lessonCount = getTutorialCatalog().entries().length;
    for (let i = 0; i < lessonCount; i++) {
      const item = document.createElement('div');
      Object.assign(item.style, {
        padding: '10px 14px', marginBottom: '2px', borderRadius: '6px', cursor: 'pointer',
        border: '1px solid transparent', transition: 'background 0.15s',
        fontSize: '12px', fontWeight: '500',
      });
      item.textContent = `${i + 1}. ${t(`tutorial.lesson.${i + 1}.name`)}`;
      item.addEventListener('mouseenter', () => {
        if (i !== this.selectedLesson) item.style.background = '#1a1f2a';
      });
      item.addEventListener('mouseleave', () => {
        if (i !== this.selectedLesson) item.style.background = 'transparent';
      });
      item.addEventListener('click', () => {
        audio.sfx.play('ui-click');
        this.selectedLesson = i;
        this.highlightListItem(listPanel, item);
        this.updateDetail();
      });
      if (i === this.selectedLesson) {
        item.style.background = '#253050';
        item.style.borderColor = '#4a90e2';
      }
      listPanel.appendChild(item);
    }
    body.appendChild(listPanel);

    // Right: detail
    const rightCol = document.createElement('div');
    Object.assign(rightCol.style, {
      flex: '1', overflow: 'auto', padding: '24px 32px',
    });

    this.thumbnailContainer = document.createElement('div');
    Object.assign(this.thumbnailContainer.style, {
      width: '400px', height: '280px',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontSize: '48px', color: '#555', userSelect: 'none',
      float: 'left', marginRight: '20px', marginBottom: '12px',
      overflow: 'hidden',
    });
    this.updateThumbnail();
    rightCol.appendChild(this.thumbnailContainer);

    this.detailContainer = document.createElement('div');
    rightCol.appendChild(this.detailContainer);
    this.updateDetail();

    body.appendChild(rightCol);
    root.appendChild(body);

    // Footer
    const footer = document.createElement('div');
    Object.assign(footer.style, {
      display: 'flex', justifyContent: 'flex-end', gap: '10px',
      padding: '14px 20px', borderTop: '1px solid #2a3140',
    });
    const imgBtn = (bg: string, w: number) => `display:inline-block;width:${w}px;height:46px;border:none;cursor:pointer;background:url('${bg}') no-repeat;background-size:100% 100%;background-color:transparent;color:#E8DCC2;font-family:'Arial Black','Trebuchet MS',Impact,sans-serif;font-weight:bold;font-size:13px;letter-spacing:2px;text-transform:uppercase;line-height:46px;text-align:center;transition:filter 0.15s,transform 0.08s`;

    const startBtn = document.createElement('button');
    startBtn.className = 'img-btn';
    startBtn.textContent = t('tutorial.startLesson');
    startBtn.style.cssText = imgBtn('/graphics/ui/confirmation-button-background.png', 180);
    startBtn.addEventListener('click', () => {
      audio.sfx.play('ui-click');
      this.launchLesson(this.selectedLesson);
    });
    footer.appendChild(startBtn);

    const backBtn = document.createElement('button');
    backBtn.className = 'img-btn';
    backBtn.textContent = t('tutorial.back');
    backBtn.style.cssText = imgBtn('/graphics/ui/cancel-button-background.png', 150);
    backBtn.addEventListener('click', () => {
      audio.sfx.play('ui-click');
      this.scene.start('MainMenu');
    });
    footer.appendChild(backBtn);
    root.appendChild(footer);

    return root;
  }

  private updateDetail(): void {
    if (!this.detailContainer) return;
    const i = this.selectedLesson + 1; // i18n keys are 1-based
    this.detailContainer.innerHTML = `
      <div style="font-size:22px;font-weight:700;margin-bottom:8px">${t(`tutorial.lesson.${i}.name`)}</div>
      <div style="font-size:14px;line-height:1.6;opacity:0.85">${t(`tutorial.lesson.${i}.desc`)}</div>
    `;
    this.updateThumbnail();
  }

  private updateThumbnail(): void {
    if (!this.thumbnailContainer) return;
    const i = this.selectedLesson + 1;
    const src = LESSON_THUMBNAILS[i];
    if (src) {
      this.thumbnailContainer.textContent = '';
      this.thumbnailContainer.innerHTML = `<img src="${src}" style="width:100%;height:100%;object-fit:contain" />`;
    } else {
      this.thumbnailContainer.innerHTML = '';
      this.thumbnailContainer.textContent = '\ud83d\udcda';
    }
  }

  private highlightListItem(listPanel: HTMLElement, active: HTMLElement): void {
    for (const child of listPanel.children) {
      const el = child as HTMLElement;
      el.style.background = 'transparent';
      el.style.borderColor = 'transparent';
    }
    active.style.background = '#253050';
    active.style.borderColor = '#4a90e2';
  }

  private launchLesson(index: number): void {
    const entry = getTutorialCatalog().entries()[index];
    if (!entry) return;
    const scenario = entry.scenario;
    const config: GameConfig = {
      scenario,
      humanPlayer: 0,
      factions: new Map([[0, { factionId: 'polska', characterId: 'a' }]]),
    };
    // Remove DOM overlay before switching scene
    this.dom?.remove();
    this.dom = null;
    // Safety: remove ALL tutorial overlays (handles stale instances from HMR/re-creation)
    document.querySelectorAll('[data-tutorial-overlay]').forEach(el => el.remove());
    this.scene.start('TurnGame', { config });
  }

  private teardown(): void {
    this.dom?.remove();
    this.dom = null;
  }
}
